// Home overview — 首頁只放「只有首頁答得出來」的東西（apps/home/specs/ia.md §0）。
//
// ⚠️ 2026-08-20 業主裁決，**推翻 2026-06-17 的「事件⇄裝置並重雙主軸」**（那句原本就寫在
// 這裡第 1 行，留著會與現況矛盾）。改判的理由：兩位實際使用者（albert、bo）獨立反映首頁
// 「怪」，診斷是**首頁沒有自己的東西——每一塊都是別的 Tab 的清單複製**：
//   - 裝置軸（`home-axis-devices`）＝攝影機 Tab 的清單 → **三平台一起移除**
//     （iOS/Android 已於 2026-08-19 移除，web 是最後一個）。
//   - 事件時間軸＝事件 Tab 的清單（web 這裡連元件都是同一支 `EventTimeline`）
//     → 換成 **事件類別統計**（albert 原話：「看要不要改成事件類別統計之類的資訊」）。
// 剩下的兩塊都不是別頁的複製：警戒卡是動作面，事件類別統計是清單答不出來的彙總。
//
// 事件列點擊 → 該時刻回放（17 §4.4）的行為沒有消失，它住在事件 Tab；首頁的統計格點下去
// 是「帶著這個分類去事件頁」。

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useDeviceList } from '../devices/useDeviceList';
import { deviceName } from '../devices/deviceModel';
import { HomeAwayControl } from './HomeAwayControl';
import { HomeEventStats, HOME_STATS_WINDOW_DAYS, type StatsDevice } from './HomeEventStats';
import './home.css';

export default function HomeOverviewPage() {
  const { t } = useTranslation();
  const { devices, error, reload } = useDeviceList();

  // `null` 一路傳到底：**載不到 ≠ 沒有**。裝置軸拿掉之後，這是首頁唯一還會反映
  // 「清單讀到了沒」的地方，退化成空陣列就會把載入中/失敗畫成「各類都 0 次」。
  const statsDevices = useMemo<StatsDevice[] | null>(
    () => (devices === null ? null : devices.map((d) => ({ id: d.device_id, name: deviceName(d) }))),
    [devices]
  );

  return (
    <div className="home-overview">
      {/* 裝置軸移除後，裝置清單的載入失敗在首頁就沒有別的地方會講了 —— 而下面的警戒卡
          拿到空清單會回報「沒有可套用的攝影機」。載不到就明講（對齊 iOS
          `home_devices_error` / Android 的同位置 ErrorBanner）。 */}
      {error && (
        <div className="card home-axis-mode" role="alert" data-testid="home-devices-error">
          <p className="error-text" style={{ marginTop: 0 }}>{t('home.loadDevicesFailed', { error })}</p>
          <button className="btn-ghost" onClick={() => void reload()}>
            {t('common.retry')}
          </button>
        </div>
      )}

      {/* 警戒卡（2026-08-19 R5 收斂）：模式 / 快捷 / 依位置自動警戒三者併為一張卡，
          不再是家頁的三個並列軸。 */}
      <HomeAwayControl devices={devices} />

      <section className="home-axis home-axis-events" aria-labelledby="home-events-h">
        <header className="home-axis-head">
          {/* 標題明寫窗口：統計只涵蓋近 7 天，不寫就會被讀成「一直以來」。 */}
          <h2 id="home-events-h">{t('home.eventStats', { days: HOME_STATS_WINDOW_DAYS })}</h2>
          <Link to="/events" className="home-axis-more">
            {t('home.allEvents')}
          </Link>
        </header>
        <HomeEventStats devices={statsDevices} deviceListFailed={Boolean(error)} />
      </section>
    </div>
  );
}
